import { map, Observable, zip } from 'rxjs';
import { useTranslation } from 'react-i18next';
import { responseHandler } from '../../responseHandler/responseHandler';
import { snackbarService } from '../snackbar/service/snackbar.service';
import { UploadButton } from './components/uploadButton.component';
import { UploadField } from './components/uploadField.component';
import { UploadListItemButton } from './components/uploadListItemButton.component';
import { filesHelper } from '../../helpers/storage/common/files.helper';

export const FileUpload = {
  Button: UploadButton,
  Field: UploadField,
  ListItemButton: UploadListItemButton,
};

export function uploadFilesToS3({
  files,
  place,
}: IUploadFileToS3Props): Observable<IUploadFileToS3Return> {
  const filesForUpload: Array<Observable<{ path: string }>> = [];
  const len = files?.length || 0;
  for (let i = 0; i < len; i++) {
    filesForUpload.push(filesHelper.upload(`${place}/${files![i].name}`, files![i]));
  }
  return zip(filesForUpload).pipe(
    map((result) => {
      return { files: result };
    }),
    responseHandler<IUploadFileToS3Return>({
      errorReturnType: { files: [] },
    }),
  );
}

/** The first file of the selection above `maxFileSize`, or `undefined` when every file fits or no limit is set. */
export const findOversizedFile = (
  files: FileList | null,
  maxFileSize?: number,
): File | undefined => {
  if (!files || !maxFileSize) return undefined;
  return Array.from(files).find((file) => file.size > maxFileSize);
};

const BYTES_PER_MB = 1024 * 1024;

/**
 * Largest file the upload controls accept unless a caller passes its own `maxFileSize`.
 *
 * Every upload goes through `filesHelper.upload` → Amplify `uploadData`, which sends anything above
 * 5 MiB as an S3 multipart upload. Completing one needs the ETag of every part, and a browser only
 * hands that header over when the bucket's CORS rule exposes it; the wawi buckets do not, so such
 * an upload fails after all parts went up (Sentry WAWIASSIST-253, WA-3945). 5 MiB is Amplify's
 * exact single-PUT ceiling, so everything up to it still goes up in one request.
 */
export const S3_UPLOAD_MAX_FILE_SIZE = 5 * BYTES_PER_MB;

/**
 * Size gate shared by the upload controls: `true` when the selection may be uploaded. Otherwise the
 * user is told which file is too big and nothing is uploaded, so a caller never sees the upload
 * fail halfway (WA-3945: Amplify sends files above 5 MB as a multipart upload the buckets cannot
 * complete).
 */
export const useFileSizeGuard = (maxFileSize: number = S3_UPLOAD_MAX_FILE_SIZE) => {
  const { t } = useTranslation();
  return (files: FileList | null): boolean => {
    const oversized = findOversizedFile(files, maxFileSize);
    if (!oversized) return true;
    snackbarService.pub.show({
      content: t('common.the_file_x_is_larger_than_x_mb', {
        name: oversized.name,
        size: Math.floor(maxFileSize / BYTES_PER_MB),
      }),
      type: 'error',
    });
    return false;
  };
};

export interface IUploadFileToS3Props {
  files: FileList | null;
  place: IUploadFileCommon['place'];
}
export interface IUploadFileToS3Return {
  files: { path: string }[];
}
export interface IUploadFileCommon extends Partial<Pick<HTMLInputElement, 'accept' | 'multiple'>> {
  place: string;
  /**
   * Largest file accepted, in bytes; `S3_UPLOAD_MAX_FILE_SIZE` (5 MiB) unless given. A bigger
   * selection is refused with a message before any upload starts.
   */
  maxFileSize?: number;
  onLoading?: () => void;
  onUploaded?: (result: IUploadFileToS3Return) => void;
}
